import { useState, useEffect, useCallback, useRef } from "react";
import { tkadlecApi } from "../../services/tkadlecApi";
import RefreshButton from "../RefreshButton/RefreshButton";
import TkadlecMemories from "../TkadlecMemories/TkadlecMemories";
import "./TkadlecSnu.css";

export default function TkadlecSnu() {
  const [tab, setTab] = useState("context");
  const [error, setError] = useState(null);
  const [contextSummary, setContextSummary] = useState(null);

  // Settings state
  const [settings, setSettings] = useState(null);
  const [settingsSaved, setSettingsSaved] = useState(false);

  // Prompts state
  const [prompts, setPrompts] = useState(null);
  const [promptsSaving, setPromptsSaving] = useState(false);
  const [promptsSaved, setPromptsSaved] = useState(false);
  const [expandedPrompt, setExpandedPrompt] = useState(null);

  const promptTextareaRef = useRef(null);

  const switchTab = (t) => { setTab(t); window.location.hash = t; };

  // Init tab from hash
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    const valid = ["context", "memories", "settings", "prompts"];
    if (valid.includes(hash)) setTab(hash);
  }, []);

  const fetchContext = useCallback(async () => {
    try {
      const data = await tkadlecApi.getContextSummary();
      setContextSummary(data);
    } catch (err) {
      console.error("Failed to fetch context:", err);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await tkadlecApi.getSettings();
      setSettings(data);
    } catch (err) {
      console.error("Failed to fetch settings:", err);
    }
  }, []);

  const fetchPrompts = useCallback(async () => {
    try {
      const data = await tkadlecApi.getPrompts();
      setPrompts(data);
    } catch (err) {
      console.error("Failed to fetch prompts:", err);
    }
  }, []);

  useEffect(() => {
    fetchContext();
    fetchSettings();
    fetchPrompts();
  }, [fetchContext, fetchSettings, fetchPrompts]);

  // Focus + auto-size prompt textarea when expanded
  useEffect(() => {
    if (expandedPrompt && promptTextareaRef.current) {
      const el = promptTextareaRef.current;
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, window.innerHeight * 0.6) + "px";
      el.focus();
    }
  }, [expandedPrompt]);


  const handleSaveSettings = async (updates) => {
    setSettingsSaved(false);
    try {
      const updated = await tkadlecApi.updateSettings(updates || settings);
      setSettings(updated);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSliderChange = (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    handleSaveSettings({ [key]: value });
  };

  const handleSavePrompts = async () => {
    if (!prompts) return;
    setPromptsSaving(true);
    setPromptsSaved(false);
    try {
      const updated = await tkadlecApi.updatePrompts({ memory_extraction: prompts.memory_extraction || "" });
      setPrompts((current) => ({ ...current, ...updated }));
      setPromptsSaved(true);
      setTimeout(() => setPromptsSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setPromptsSaving(false);
    }
  };

  /* ── Render ── */

  return (
    <div className="tkadlec-snu">
      <div className="tkadlec-header">
        <div>
          <h2>🔮 Tkadlec Snu</h2>
          <p className="tkadlec-subtitle">Memory context and extraction settings</p>
        </div>
        <RefreshButton
          onClick={async () => {
            await Promise.all([fetchContext(), fetchSettings(), fetchPrompts()]);
          }}
          title="Refresh Tkadlec data"
        />
      </div>

      <div className="tkadlec-tabs">
        {["context", "memories", "prompts", "settings"].map((t) => (
          <button
            key={t}
            className={`tab-btn ${tab === t ? "active" : ""}`}
            onClick={() => switchTab(t)}
          >
            {t === "context" && "🧠 Context"}
            {t === "memories" && "Memories"}
            {t === "prompts" && "📝 Prompts"}
            {t === "settings" && "⚙️ Settings"}
          </button>
        ))}
      </div>

      {error && <div className="tkadlec-error">{error}</div>}

      {/* ── Context Tab ── */}
      {tab === "context" && (
        <div className="tkadlec-panel">
          <h3>Lightweight Context Summary</h3>
          <p className="context-description">
            This is the context summary that gets injected into Anna's system prompt for cross-session awareness.
            Updated every 15 minutes.
          </p>
          {contextSummary?.summary ? (
            <div className="context-content">
              <pre>{contextSummary.summary}</pre>
              <div className="context-meta">
                <span>Messages analyzed: {contextSummary.messageCount || "?"}</span>
                <span>Window: {contextSummary.windowDays || "?"} days</span>
                <span>Updated: {contextSummary.updatedAt ? new Date(contextSummary.updatedAt).toLocaleString() : "?"}</span>
              </div>
            </div>
          ) : (
            <p className="empty-state">No context summary available yet. The context builder runs every 15 minutes.</p>
          )}
        </div>
      )}

      {tab === "memories" && <TkadlecMemories />}

      {tab === "settings" && (
        <div className="tkadlec-panel">
          <h3>Settings</h3>
          {settings ? (
            <>
              <div className="setting-group">
                <label>OpenAI API Key</label>
                <input
                  type="password"
                  placeholder={settings.hasApiKey ? "••••••••••••••••" : "sk-..."}
                  onBlur={(e) => {
                    if (e.target.value.trim()) {
                      handleSaveSettings({ apiKey: e.target.value.trim() });
                      e.target.value = "";
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.value.trim()) {
                      handleSaveSettings({ apiKey: e.target.value.trim() });
                      e.target.value = "";
                    }
                  }}
                />
                <span className="setting-help">
                  {settings.hasApiKey ? "Key is set. Enter a new one to replace it." : "Required for memory extraction."}
                </span>
              </div>
              <div className="setting-group">
                <label>Temperature: {settings.temperature?.toFixed(2)}</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={settings.temperature ?? 0.4}
                  onChange={(e) => handleSliderChange("temperature", parseFloat(e.target.value))}
                />
                <span className="setting-help">Lower = more focused, higher = more creative</span>
              </div>
              {settingsSaved && <span className="save-indicator">✓ Saved</span>}
            </>
          ) : (
            <p className="empty-state">Loading settings…</p>
          )}
        </div>
      )}

      {tab === "prompts" && (
        <div className="tkadlec-panel">
          <h3>Extraction Prompt</h3>
          {prompts ? (
            <>
              {[
                { key: "memory_extraction", label: "Memory Prompt", desc: "Extracts, compares, merges, and cleans shadow memories with GPT-5.6 Terra" },
              ].map(({ key, label, desc }) => (
                <div key={key} className={`prompt-group ${expandedPrompt === key ? "expanded" : "collapsed"}`}>
                  <div className="prompt-header" onClick={() => setExpandedPrompt(expandedPrompt === key ? null : key)}>
                    <span className="prompt-collapse-icon">{expandedPrompt === key ? "▼" : "▶"}</span>
                    <label className="prompt-label">{label}</label>
                  </div>
                  {expandedPrompt === key && (
                    <>
                      <span className="prompt-desc">{desc}</span>
                      <textarea
                        ref={promptTextareaRef}
                        className="prompt-textarea"
                        value={prompts[key] || ""}
                        onChange={(e) => setPrompts({ ...prompts, [key]: e.target.value })}
                      />
                    </>
                  )}
                </div>
              ))}
              <div className="setting-actions">
                <button
                  className="btn-primary"
                  onClick={handleSavePrompts}
                  disabled={promptsSaving}
                >
                  {promptsSaving ? "Saving…" : "Save Prompts"}
                </button>
                {promptsSaved && <span className="save-indicator">✓ Saved</span>}
              </div>
            </>
          ) : (
            <p className="empty-state">Loading prompts…</p>
          )}
        </div>
      )}
    </div>
  );
}
